import { QueryClient } from '@tanstack/react-query';
import { ApiClientError } from './http.js';

/**
 * TanStack Query owns all server state (catalogs, cart, orders, settings).
 * Zustand owns only true client state (see `src/stores/`).
 *
 * Rationale: nearly everything on this site is a cached copy of something the
 * API owns. Putting that in Redux/Context means hand-rolling caching,
 * invalidation, deduplication and loading/error states for every resource.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry(failureCount, error) {
        // Never retry a client error — a 404 or 422 will not fix itself.
        if (error instanceof ApiClientError && error.status >= 400 && error.status < 500) {
          return false;
        }
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * Central query-key factory. Keeping every key in one place is what makes
 * invalidation after a mutation reliable — no more guessing whether the cart
 * badge listens to ['cart'] or ['cart', userId].
 */
export const keys = {
  auth: { me: ['auth', 'me'] },
  cart: ['cart'],
  secondhand: {
    all: ['books', 'secondhand'],
    list: (params) => ['books', 'secondhand', 'list', params],
    detail: (slug) => ['books', 'secondhand', 'detail', slug],
  },
  publisher: {
    all: ['books', 'publisher'],
    list: (params) => ['books', 'publisher', 'list', params],
    detail: (slug) => ['books', 'publisher', 'detail', slug],
  },
  taxonomy: {
    categories: ['taxonomy', 'categories'],
    publishers: ['taxonomy', 'publishers'],
  },
  orders: {
    mine: ['orders', 'mine'],
    detail: (id) => ['orders', 'detail', id],
  },
  subscription: {
    plan: ['subscription', 'plan'],
    mine: ['subscription', 'mine'],
  },
  admin: {
    /* Two catalogs, two key namespaces. `kind` sits in the key rather than in a
       filter object so that invalidating secondhand can never refetch — or
       worse, overwrite — the publisher list. */
    books: {
      all: (kind) => ['admin', 'books', kind],
      list: (kind, params) => ['admin', 'books', kind, 'list', params],
      detail: (kind, id) => ['admin', 'books', kind, 'detail', id],
    },
    categories: ['admin', 'categories'],
    publishers: ['admin', 'publishers'],
    users: (params) => ['admin', 'users', params],
    user: (id) => ['admin', 'users', id],
    orders: (params) => ['admin', 'orders', params],
    promoCodes: ['admin', 'promo-codes'],
    promotions: ['admin', 'promotions'],
    auditLog: (params) => ['admin', 'audit-log', params],
    dashboard: ['admin', 'dashboard'],
  },
};

export default queryClient;
